import { r as registerInstance, h } from './index-4df07f2a.js';
import { u as isSymbol_1, n as isObject_1, r as get_1 } from './get-f8984301.js';
import { i as isArrayLike_1, f as _baseIteratee, k as keys_1, m as map_1 } from './map-8afd9017.js';

/**
 * Creates a `_.find` or `_.findLast` function.
 *
 * @private
 * @param {Function} findIndexFunc The function to find the collection index.
 * @returns {Function} Returns the new find function.
 */
function createFind(findIndexFunc) {
  return function(collection, predicate, fromIndex) {
    var iterable = Object(collection);
    if (!isArrayLike_1(collection)) {
      var iteratee = _baseIteratee(predicate);
      collection = keys_1(collection);
      predicate = function(key) { return iteratee(iterable[key], key, iterable); };
    }
    var index = findIndexFunc(collection, predicate, fromIndex);
    return index > -1 ? iterable[iteratee ? collection[index] : index] : undefined;
  };
}

var _createFind = createFind;

/**
 * The base implementation of `_.findIndex` and `_.findLastIndex` without
 * support for iteratee shorthands.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {Function} predicate The function invoked per iteration.
 * @param {number} fromIndex The index to search from.
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function baseFindIndex(array, predicate, fromIndex, fromRight) {
  var length = array.length,
      index = fromIndex + (fromRight ? 1 : -1);

  while ((fromRight ? index-- : ++index < length)) {
    if (predicate(array[index], index, array)) {
      return index;
    }
  }
  return -1;
}

var _baseFindIndex = baseFindIndex;

/** Used to match a single whitespace character. */
var reWhitespace = /\s/;

/**
 * Used by `_.trim` and `_.trimEnd` to get the index of the last non-whitespace
 * character of `string`.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {number} Returns the index of the last non-whitespace character.
 */
function trimmedEndIndex(string) {
  var index = string.length;

  while (index-- && reWhitespace.test(string.charAt(index))) {}
  return index;
}

var _trimmedEndIndex = trimmedEndIndex;

/** Used to match leading whitespace. */
var reTrimStart = /^\s+/;

/**
 * The base implementation of `_.trim`.
 *
 * @private
 * @param {string} string The string to trim.
 * @returns {string} Returns the trimmed string.
 */
function baseTrim(string) {
  return string
    ? string.slice(0, _trimmedEndIndex(string) + 1).replace(reTrimStart, '')
    : string;
}

var _baseTrim = baseTrim;

/** Used as references for various `Number` constants. */
var NAN = 0 / 0;

/** Used to detect bad signed hexadecimal string values. */
var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

/** Used to detect binary string values. */
var reIsBinary = /^0b[01]+$/i;

/** Used to detect octal string values. */
var reIsOctal = /^0o[0-7]+$/i;

/** Built-in method references without a dependency on `root`. */
var freeParseInt = parseInt;

/**
 * Converts `value` to a number.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {number} Returns the number.
 * @example
 *
 * _.toNumber(3.2);
 * // => 3.2
 *
 * _.toNumber(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toNumber(Infinity);
 * // => Infinity
 *
 * _.toNumber('3.2');
 * // => 3.2
 */
function toNumber(value) {
  if (typeof value == 'number') {
    return value;
  }
  if (isSymbol_1(value)) {
    return NAN;
  }
  if (isObject_1(value)) {
    var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
    value = isObject_1(other) ? (other + '') : other;
  }
  if (typeof value != 'string') {
    return value === 0 ? value : +value;
  }
  value = _baseTrim(value);
  var isBinary = reIsBinary.test(value);
  return (isBinary || reIsOctal.test(value))
    ? freeParseInt(value.slice(2), isBinary ? 2 : 8)
    : (reIsBadHex.test(value) ? NAN : +value);
}

var toNumber_1 = toNumber;

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0,
    MAX_INTEGER = 1.7976931348623157e+308;

/**
 * Converts `value` to a finite number.
 *
 * @static
 * @memberOf _
 * @since 4.12.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {number} Returns the converted number.
 * @example
 *
 * _.toFinite(3.2);
 * // => 3.2
 *
 * _.toFinite(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toFinite(Infinity);
 * // => 1.7976931348623157e+308
 *
 * _.toFinite('3.2');
 * // => 3.2
 */
function toFinite(value) {
  if (!value) {
    return value === 0 ? value : 0;
  }
  value = toNumber_1(value);
  if (value === INFINITY || value === -INFINITY) {
    var sign = (value < 0 ? -1 : 1);
    return sign * MAX_INTEGER;
  }
  return value === value ? value : 0;
}

var toFinite_1 = toFinite;

/**
 * Converts `value` to an integer.
 *
 * **Note:** This method is loosely based on
 * [`ToInteger`](http://www.ecma-international.org/ecma-262/7.0/#sec-tointeger).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {number} Returns the converted integer.
 * @example
 *
 * _.toInteger(3.2);
 * // => 3
 *
 * _.toInteger(Number.MIN_VALUE);
 * // => 0
 *
 * _.toInteger(Infinity);
 * // => 1.7976931348623157e+308
 *
 * _.toInteger('3.2');
 * // => 3
 */
function toInteger(value) {
  var result = toFinite_1(value),
      remainder = result % 1;

  return result === result ? (remainder ? result - remainder : result) : 0;
}

var toInteger_1 = toInteger;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax = Math.max;

/**
 * This method is like `_.find` except that it returns the index of the first
 * element `predicate` returns truthy for instead of the element itself.
 *
 * @static
 * @memberOf _
 * @since 1.1.0
 * @category Array
 * @param {Array} array The array to inspect.
 * @param {Function} [predicate=_.identity] The function invoked per iteration.
 * @param {number} [fromIndex=0] The index to search from.
 * @returns {number} Returns the index of the found element, else `-1`.
 * @example
 *
 * var users = [
 *   { 'user': 'barney',  'active': false },
 *   { 'user': 'fred',    'active': false },
 *   { 'user': 'pebbles', 'active': true }
 * ];
 *
 * _.findIndex(users, function(o) { return o.user == 'barney'; });
 * // => 0
 *
 * // The `_.matches` iteratee shorthand.
 * _.findIndex(users, { 'user': 'fred', 'active': false });
 * // => 1
 *
 * // The `_.matchesProperty` iteratee shorthand.
 * _.findIndex(users, ['active', false]);
 * // => 0
 *
 * // The `_.property` iteratee shorthand.
 * _.findIndex(users, 'active');
 * // => 2
 */
function findIndex(array, predicate, fromIndex) {
  var length = array == null ? 0 : array.length;
  if (!length) {
    return -1;
  }
  var index = fromIndex == null ? 0 : toInteger_1(fromIndex);
  if (index < 0) {
    index = nativeMax(length + index, 0);
  }
  return _baseFindIndex(array, _baseIteratee(predicate), index);
}

var findIndex_1 = findIndex;

/**
 * Iterates over elements of `collection`, returning the first element
 * `predicate` returns truthy for. The predicate is invoked with three
 * arguments: (value, index|key, collection).
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Collection
 * @param {Array|Object} collection The collection to inspect.
 * @param {Function} [predicate=_.identity] The function invoked per iteration.
 * @param {number} [fromIndex=0] The index to search from.
 * @returns {*} Returns the matched element, else `undefined`.
 * @example
 *
 * var users = [
 *   { 'user': 'barney',  'age': 36, 'active': true },
 *   { 'user': 'fred',    'age': 40, 'active': false },
 *   { 'user': 'pebbles', 'age': 1,  'active': true }
 * ];
 *
 * _.find(users, function(o) { return o.age < 40; });
 * // => object for 'barney'
 *
 * // The `_.matches` iteratee shorthand.
 * _.find(users, { 'age': 1, 'active': true });
 * // => object for 'pebbles'
 *
 * // The `_.matchesProperty` iteratee shorthand.
 * _.find(users, ['active', false]);
 * // => object for 'fred'
 *
 * // The `_.property` iteratee shorthand.
 * _.find(users, 'active');
 * // => object for 'barney'
 */
var find = _createFind(findIndex_1);

var find_1 = find;

const scmpMenuMainMenuDefaultContextCss = ".scmp-menu-main-menu-default-context{background-color:#222834}";

let ScmpMenuMainMenuDefaultContext = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.isDataLoaded = false;
    this.defaultTabData = {
      lifestyle: {
        allowedPaths: [
          "/lifestyle",
          "/news/people-culture",
          "/magazines/style",
          "/magazines/post-magazine",
          "/k-pop",
          "/sport/outdoor",
          "/better-life",
        ],
        tabName: "Lifestyle",
      },
    };
    this.queues = {
      hk: 'related_topic_homepage_hk_',
      int: 'related_topic_homepage_int_',
      us: 'related_topic_homepage_us_',
      asia: 'related_topic_homepage_asia_',
    };
  }
  get theme() {
    var _a;
    return (_a = this.menuConfig) === null || _a === void 0 ? void 0 : _a.theme;
  }
  get edition() {
    var _a;
    return (_a = this.menuConfig) === null || _a === void 0 ? void 0 : _a.edition;
  }
  get path() {
    var _a;
    return (_a = this.menuConfig) === null || _a === void 0 ? void 0 : _a.path;
  }
  get defaultTab() {
    var _a;
    if ((_a = this.menuConfig) === null || _a === void 0 ? void 0 : _a.defaultTab) {
      return this.menuConfig.defaultTab;
    }
    if (this.path) {
      return this.getFirstDefaultTab(this.path);
    }
    return null;
  }
  get containerStyle() {
    const output = {};
    if (this.menuConfig.containerMinHeight) {
      output.minHeight = this.menuConfig.containerMinHeight;
    }
    return output;
  }
  get d8Endpoint() {
    return find_1(this.endpoints, ['name', 'd8']);
  }
  get csEndpoint() {
    return find_1(this.endpoints, ['name', 'cs']);
  }
  get csV2Endpoint() {
    return find_1(this.endpoints, ['name', 'cs-v2']);
  }
  getFirstDefaultTab(path) {
    var _a, _b;
    return (_b = (_a = Object.values(this.defaultTabData).find((item) => item.allowedPaths.some((p) => path.startsWith(p)))) === null || _a === void 0 ? void 0 : _a.tabName) !== null && _b !== void 0 ? _b : "";
  }
  async fetchTopics() {
    if (this.csV2Endpoint) {
      return this.fetchTopicsFromContentServiceV2();
    }
    if (this.csEndpoint) {
      return this.fetchTopicsFromContentService();
    }
    return Promise.resolve();
  }
  async fetchTopicsFromContentServiceV2() {
    if (this.csV2Endpoint) {
      const csTopicsResponse = await fetch(this.csV2Endpoint.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apiKey': this.csV2Endpoint.apiKey,
        },
        body: JSON.stringify({
          query: `
            query burgerMenuTopics($name: String!) {
              queue(filter: { name: $name }) {
                items(first: 10) {
                  edges {
                    node {
                      __typename
                      ... on Topic {
                        name
                        urlAlias
                        entityUuid
                        entityId
                        images (filter: { type: { exclude: [] } }) {
                          style(filter: {style: "300x250"}) {
                            url
                          }
                          title
                        }
                      }
                    }
                  }
                }
              }
            }
          `,
          variables: {
            name: this.queues[this.edition],
          },
        }),
      });
      const csTopicsResult = await csTopicsResponse.json();
      return map_1(get_1(csTopicsResult, 'data.queue.items.edges', []), (item) => {
        const { __typename, name, urlAlias, entityUuid, entityId, images = [] } = get_1(item, 'node') || {};
        return {
          __typename,
          urlAlias,
          entityUuid,
          entityId,
          text: name,
          image: {
            src: get_1(images, '0.style.url')
          }
        };
      });
    }
    return [];
  }
  async fetchTopicsFromContentService() {
    if (this.csEndpoint) {
      const csTopicsResponse = await fetch(this.csEndpoint.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apiKey': this.csEndpoint.apiKey,
        },
        body: JSON.stringify({
          query: `
            query burgerMenuTopics($name: String!) {
              queue(filter: { name: $name }) {
                items {
                  __typename
                  ... on Topic {
                    name
                    urlAlias
                    entityUuid
                    entityId
                    images {
                      style(filter: {style: "300x250"}) {
                        url
                      }
                      title
                    }
                  }
                }
              }
            }
          `,
          variables: {
            name: this.queues[this.edition],
          },
        }),
      });
      const csTopicsResult = await csTopicsResponse.json();
      return map_1(get_1(csTopicsResult, 'data.queue.items', []), ({ __typename, name, urlAlias, entityUuid, entityId, images = [] }) => {
        return {
          __typename,
          urlAlias,
          entityUuid,
          entityId,
          text: name,
          image: {
            src: get_1(images, '0.style.url')
          }
        };
      });
    }
    return [];
  }
  async fetchDrupalAppConfigFromContentService() {
    const endpoint = this.csV2Endpoint || this.csEndpoint || '';
    try {
      if (endpoint) {
        const csDrupalAppConfigResponse = await fetch(endpoint.url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apiKey': endpoint.apiKey,
          },
          body: JSON.stringify({
            query: `
              query appConfig($entityId: String) {
                appConfig(filter: { entityId: $entityId }) {
                  json
                }
              }
            `,
            variables: {
              entityId: 'scmp_pwa_burger_menu'
            },
          }),
        });
        const csMenuResult = await csDrupalAppConfigResponse.json();
        return get_1(csMenuResult, 'data.appConfig.json', null);
      }
    }
    catch (_a) {
      return null;
    }
    return null;
  }
  componentWillLoad() {
    // Change it to not using async await while using promise.then to allow the placeholder component to be rendered first and show the component when data is ready
    this.fetchTopics().then((topics) => {
      const topicItems = topics;
      this.fetchDrupalAppConfigFromContentService().then((drupalAppConfigJson) => {
        this.topTab = get_1(drupalAppConfigJson, `default[${this.theme}].topTab`);
        const tabs = get_1(drupalAppConfigJson, `default[${this.theme}].tabs`);
        this.tabs = map_1(tabs, (tab) => {
          var _a;
          const isActive = this.defaultTab ? (this.defaultTab === ((_a = tab === null || tab === void 0 ? void 0 : tab.button) === null || _a === void 0 ? void 0 : _a.text)) : tab.isActive;
          if (tab.type === 'topics') {
            return Object.assign(Object.assign({}, tab), { items: topicItems, isActive });
          }
          return Object.assign(Object.assign({}, tab), { isActive });
        });
        this.isDataLoaded = true;
      });
    });
  }
  getMenuContent() {
    if (this.isDataLoaded) {
      return (h("div", { class: "scmp-menu-main-menu-default-context", style: this.containerStyle }, h("scmp-menu-main-menu", { tabs: this.tabs, topTab: this.topTab, menuConfig: Object.assign({}, this.menuConfig), editionList: this.editionList, showBackToV1Button: this.showBackToV1Button, showBackToV2Button: this.showBackToV2Button })));
    }
    else {
      return h("scmp-menu-main-menu-placeholder", null);
    }
  }
  render() {
    return this.getMenuContent();
  }
};
ScmpMenuMainMenuDefaultContext.style = scmpMenuMainMenuDefaultContextCss;

export { ScmpMenuMainMenuDefaultContext as scmp_menu_main_menu_default_context };
